/**
 * Local video editor project files: timeline + story/scenes snapshot for reopening.
 * Save: File System Access API folder picker when available, else JSON download.
 */

import { persistedMediaUrl } from '@/lib/mediaUrls';
import { normalizeVideoExportFraming } from '@/lib/videoExportFraming';
import { normalizeVideoBookends, persistVideoBookendsForStorage } from '@/lib/videoEditorBookends';
import { normalizeVideoClipEmbeddedAudioMute } from '@/lib/videoEditorPlayback';
import { clampEditorAnimationSpeed, EDITOR_ANIMATION_SPEED_DEFAULT } from '@/lib/editorAnimationSettings';
import {
  normalizeSubtitleLayer,
  subtitleLayerFromLegacyAnchors,
} from '@/lib/subtitles/subtitleLayer';
import { applyExtendedSubtitleStyleFields } from '@/lib/subtitles/subtitleStyleConfig';
import {
  SUBTITLE_MAX_FONT_SIZE,
  SUBTITLE_MAX_OUTLINE_WIDTH,
  SUBTITLE_MIN_FONT_SIZE,
} from '@/lib/videoSubtitlePreview';

export const VIDEO_PROJECT_FORMAT = 'twin-tales-video-project';
export const VIDEO_PROJECT_VERSION = 1;

/** Default subtitle overlay (preview + export). Coordinates are normalized 0–1 over the video frame. */
export const DEFAULT_VIDEO_SUBTITLE_STYLE = {
  anchorX: 0.5,
  anchorY: 0.88,
  fontSize: 26,
  fontFamily: 'Impact',
  color: '#ffffff',
  outlineWidth: 3,
  outlineColor: '#000000',
  bold: true,
  italic: false,
  underline: false,
  letterSpacing: 0,
  shadowColor: '#ffff00',
  shadowBlur: 0,
  backgroundColor: '#000000',
  backgroundOpacity: 0,
  animation: 'none',
  spokenWordWindow: true,
  spokenWordLimit: 5,
  spokenWordHighlightColor: '#d4ff00',
};

function clampNum(n, lo, hi, fb) {
  const v = Number(n);
  return Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : fb;
}

export function normalizeVideoSubtitleStyle(raw, aspectRatio = '16:9') {
  const d = DEFAULT_VIDEO_SUBTITLE_STYLE;
  if (!raw || typeof raw !== 'object') {
    return {
      ...d,
      layer: subtitleLayerFromLegacyAnchors(d.anchorX, d.anchorY, aspectRatio),
    };
  }
  const anchorX = clampNum(raw.anchorX ?? raw.anchor_x, 0.02, 0.98, d.anchorX);
  const anchorY = clampNum(raw.anchorY ?? raw.anchor_y, 0.02, 0.98, d.anchorY);
  const layer = raw.layer
    ? normalizeSubtitleLayer(raw.layer, aspectRatio)
    : subtitleLayerFromLegacyAnchors(anchorX, anchorY, aspectRatio);
  return applyExtendedSubtitleStyleFields({
    anchorX,
    anchorY,
    fontSize: Math.round(clampNum(raw.fontSize ?? raw.font_size, SUBTITLE_MIN_FONT_SIZE, SUBTITLE_MAX_FONT_SIZE, d.fontSize)),
    fontFamily: raw.fontFamily ?? raw.font_family ?? d.fontFamily,
    color: typeof raw.color === 'string' && raw.color.trim() ? raw.color.trim() : d.color,
    outlineWidth: clampNum(raw.outlineWidth ?? raw.outline_width, 0, SUBTITLE_MAX_OUTLINE_WIDTH, d.outlineWidth),
    outlineColor:
      typeof raw.outlineColor === 'string' && raw.outlineColor.trim()
        ? raw.outlineColor.trim()
        : typeof raw.outline_color === 'string' && raw.outline_color.trim()
          ? raw.outline_color.trim()
          : d.outlineColor,
    bold: Boolean(raw.bold),
    layer,
  }, raw);
}

/** ISO-ish codes → label for version history / filenames (align with editor LANGUAGE_OPTIONS). */
const VIDEO_PROJECT_LANGUAGE_LABELS = {
  en: 'English',
  hi: 'Hindi',
  kn: 'Kannada',
  mr: 'Marathi',
  ta: 'Tamil',
  de: 'German',
};

/** Display name for `story.language` on saved project snapshots; unknown codes returned trimmed. */
export function formatVideoProjectLanguageLabel(code) {
  if (!code || typeof code !== 'string') return '';
  const key = code.trim().toLowerCase().slice(0, 8);
  return VIDEO_PROJECT_LANGUAGE_LABELS[key] || code.trim();
}

function measureScriptSampleSignals(scenes, extraSampleTexts = []) {
  const chunks = [];
  for (const s of Array.isArray(scenes) ? scenes : []) {
    chunks.push(String(s.scene_text || s.narration_text || s.text || '').trim());
  }
  for (const t of extraSampleTexts || []) {
    const x = typeof t === 'string' ? t.trim() : '';
    if (x) chunks.push(x);
  }
  const combined = chunks.join('\n').trim();
  if (!combined) {
    return { combined: '', devanagari: 0, tamil: 0, kannada: 0, latinLetters: 0 };
  }

  let devanagari = 0;
  let tamil = 0;
  let kannada = 0;
  let latinLetters = 0;
  for (let i = 0; i < combined.length; i++) {
    const c = combined.charCodeAt(i);
    if (c >= 0x0900 && c <= 0x097f) devanagari += 1;
    else if (c >= 0x0b80 && c <= 0x0bff) tamil += 1;
    else if (c >= 0x0c80 && c <= 0x0cff) kannada += 1;
    else if ((c >= 65 && c <= 90) || (c >= 97 && c <= 122)) latinLetters += 1;
  }

  return { combined, devanagari, tamil, kannada, latinLetters };
}

/** Guess ISO-ish language code from visible script when `segment_language` is missing (legacy segments). */
export function inferLanguageFromScriptSamples(scenes, extraSampleTexts = []) {
  const { combined, devanagari, tamil, kannada, latinLetters } = measureScriptSampleSignals(
    scenes,
    extraSampleTexts,
  );
  if (!combined) return '';

  const indic = devanagari + tamil + kannada;
  if (tamil >= 5 && tamil >= kannada && tamil >= devanagari * 0.25) return 'ta';
  if (kannada >= 5 && kannada >= devanagari * 0.25) return 'kn';
  if (devanagari >= 5 && devanagari >= latinLetters * 0.15) return 'hi';

  const englishish =
    indic === 0 && (latinLetters >= 8 || (latinLetters >= 4 && combined.replace(/\s/g, '').length >= 14));
  if (englishish) return 'en';

  return '';
}

/**
 * Canonical script language for narration/video projects.
 * 1) Unanimous `segment_language` when every scene has an explicit code — unless Latin script clearly
 *    contradicts stale Hindi/Marathi codes (edited English body, DB metadata not updated).
 * 2) Else infer from visible script.
 * 3) `story.language`, then `story.input.language`, then first partial segment_language.
 */
export function resolveVideoProjectScriptLanguage(story, scenes, extraSampleTexts = []) {
  const norm = (v) => {
    if (v == null || v === '') return '';
    const s = String(v).trim().toLowerCase().slice(0, 8);
    return s || '';
  };

  const list = Array.isArray(scenes) ? scenes : [];
  const langs = list.map((s) => norm(s.segment_language));

  const inferred = inferLanguageFromScriptSamples(list, extraSampleTexts);
  const signals = measureScriptSampleSignals(list, extraSampleTexts);

  if (list.length && langs.every(Boolean) && langs.every((c) => c === langs[0])) {
    const unanimous = langs[0];
    const staleLatinEnglishOverridesHiMr =
      (unanimous === 'hi' || unanimous === 'mr')
      && inferred === 'en'
      && signals.devanagari === 0
      && signals.latinLetters >= 12;
    if (!staleLatinEnglishOverridesHiMr) return unanimous;
  }

  if (inferred) return inferred;

  const fromStory = norm(story?.language);
  if (fromStory) return fromStory;

  const fromInput = norm(story?.input?.language);
  if (fromInput) return fromInput;

  const codes = langs.filter(Boolean);
  if (codes.length) return codes[0];

  return '';
}

const PENDING_PROJECT_KEY = 'twinTalesPendingVideoProject';

/** Safe folder / filename segment (Windows + POSIX). */
export function sanitizeProjectFolderName(name) {
  if (!name || typeof name !== 'string') return 'Untitled';
  return name
    .replace(/[/\\?%*:|"<>.\u0000-\u001f]/g, '_')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80) || 'Untitled';
}

export function canUseDirectoryPicker() {
  return typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';
}

/**
 * Copy narration audio URL + duration from timeline clips into each scene row and fill video
 * clip `audioUrl` from the audio lane when missing — so saved versions/project.json round-trip.
 */
export function enrichProjectDocWithTimelineAudio(docLike) {
  const scenes = Array.isArray(docLike.scenes) ? JSON.parse(JSON.stringify(docLike.scenes)) : [];
  const videoTracks = JSON.parse(JSON.stringify(docLike.editor?.videoTracks || [[]]));
  const audioTracks = JSON.parse(JSON.stringify(docLike.editor?.audioTracks || [[]]));
  const vFlat = videoTracks.flat();
  const aFlat = audioTracks.flat();
  const laneUrlBySceneId = {};
  const laneDurBySceneId = {};
  aFlat.forEach((ac) => {
    const sid = String(ac.sceneId ?? '').replace(/^audio-/, '');
    if (!sid) return;
    if (ac.url) laneUrlBySceneId[sid] = ac.url;
    const d = Number(ac.sourceDuration ?? ac.duration);
    if (Number.isFinite(d) && d > 0) laneDurBySceneId[sid] = d;
  });

  const enrichedScenes = scenes.map((scene) => {
    const sid = scene.id != null ? String(scene.id) : '';
    if (!sid) return { ...scene };
    const vClip = vFlat.find((c) => String(c.sceneId || c.id) === sid);
    const timelineUrl = vClip?.audioUrl || laneUrlBySceneId[sid] || scene.audio_url || null;
    const dur = Number(
      laneDurBySceneId[sid]
      ?? vClip?.playbackDuration
      ?? vClip?.duration
      ?? scene.audio_duration
      ?? scene.audio_duration_seconds
      ?? scene.duration_seconds,
    );
    const next = { ...scene };
    if (timelineUrl) next.audio_url = timelineUrl;
    if (Number.isFinite(dur) && dur > 0) {
      next.audio_duration = dur;
      next.audio_duration_seconds = dur;
    }
    return next;
  });

  const enrichedVideoTracks = videoTracks.map((track) =>
    track.map((c) => {
      if (!c.sceneId) return { ...c };
      const sid = String(c.sceneId);
      const laneUrl = laneUrlBySceneId[sid];
      if (laneUrl && !c.audioUrl) return { ...c, audioUrl: laneUrl };
      return { ...c };
    }),
  );

  return {
    ...docLike,
    scenes: enrichedScenes,
    editor: {
      ...docLike.editor,
      videoTracks: enrichedVideoTracks,
      audioTracks,
    },
  };
}

/** Strip blob: previews, migrate full API URLs → server-relative paths, keep media history usable offline. */
function persistScenePrimaryAndHistory(scene) {
  if (!scene || typeof scene !== 'object') return scene;
  const next = { ...scene };
  ['video_url', 'audio_url', 'image_url'].forEach((k) => {
    if (!next[k]) return;
    const p = persistedMediaUrl(next[k]);
    next[k] = p || next[k];
  });
  ['video_history', 'audio_history', 'image_history'].forEach((k) => {
    if (!Array.isArray(next[k])) return;
    next[k] = next[k].map((entry) => {
      if (typeof entry === 'string') {
        const p = persistedMediaUrl(entry);
        return p ?? entry;
      }
      if (entry && typeof entry === 'object' && entry.url) {
        const p = persistedMediaUrl(entry.url);
        return { ...entry, url: p ?? entry.url };
      }
      return entry;
    });
  });
  return next;
}

function finalizeDocPersistedMedia(docLike) {
  const scenesRaw = Array.isArray(docLike.scenes) ? docLike.scenes : [];
  const scenes = scenesRaw.map((s) => persistScenePrimaryAndHistory(s));
  const sceneById = Object.fromEntries(scenes.map((s) => [String(s.id), s]));

  const sanitizeClipUrl = (u) =>
    typeof u === 'string' && u.startsWith('blob:') ? null : u;

  const videoTracks = (docLike.editor?.videoTracks || [[]]).map((track) =>
    track.map((c) => {
      const sid = String(c.sceneId ?? c.id ?? '');
      const scene = sid ? sceneById[sid] : null;
      let videoUrl = sanitizeClipUrl(c.videoUrl);
      let imageUrl = sanitizeClipUrl(c.imageUrl);
      let audioUrl = sanitizeClipUrl(c.audioUrl);
      if (!videoUrl && scene?.video_url) videoUrl = scene.video_url;
      if (!imageUrl && scene?.image_url) imageUrl = scene.image_url;
      if (!audioUrl && scene?.audio_url) audioUrl = scene.audio_url;
      return normalizeVideoClipEmbeddedAudioMute({
        ...c,
        videoUrl: videoUrl ? persistedMediaUrl(videoUrl) || videoUrl : null,
        imageUrl: imageUrl ? persistedMediaUrl(imageUrl) || imageUrl : null,
        audioUrl: audioUrl ? persistedMediaUrl(audioUrl) || audioUrl : null,
      });
    }),
  );

  const audioTracks = (docLike.editor?.audioTracks || [[]]).map((track) =>
    track.map((ac) => {
      const sid = String(ac.sceneId ?? '').replace(/^audio-/, '');
      const scene = sid ? sceneById[sid] : null;
      let url = sanitizeClipUrl(ac.url);
      if (!url && scene?.audio_url) url = scene.audio_url;
      return {
        ...ac,
        url: url ? persistedMediaUrl(url) || url : null,
      };
    }),
  );

  const storyMusic =
    typeof docLike.story?.music_url === 'string' ? docLike.story.music_url : null;
  const musicTracks = (docLike.editor?.musicTracks || [[]]).map((track) =>
    track.map((mc) => {
      let url = sanitizeClipUrl(mc.url);
      if (!url && storyMusic) url = storyMusic;
      return {
        ...mc,
        url: url ? persistedMediaUrl(url) || url : null,
      };
    }),
  );

  const subtitleTracks = (docLike.editor?.subtitleTracks || [[]]).map((track) =>
    track.map((c) => ({ ...c })),
  );

  const subtitleStyle = normalizeVideoSubtitleStyle(docLike.editor?.subtitleStyle);
  const bookendOpts = {
    defaultTitle: docLike.story?.title || '',
    aspectRatio: docLike.editor?.exportAspectRatio || '16:9',
  };
  const videoBookends = persistVideoBookendsForStorage(
    docLike.editor?.videoBookends,
    bookendOpts,
  );

  return {
    ...docLike,
    scenes,
    editor: {
      ...docLike.editor,
      videoTracks,
      audioTracks,
      musicTracks,
      subtitleTracks,
      subtitleStyle,
      videoBookends,
      overlayTracks: docLike.editor?.overlayTracks || [[]],
    },
  };
}

/**
 * Build serializable project document (blob: URLs reconciled via scene/canonical refs when possible).
 */
export function buildVideoProjectDocument({
  storyId,
  story,
  scenes,
  videoTracks,
  audioTracks,
  musicTracks,
  subtitleTracks,
  effectTracks,
  overlayTracks = null,
  subtitleStyle,
  timelineZoom,
  timelineActive,
  volume,
  masterAudioPlaybackSpeed,
  isMuted,
  videoMasterMuted,
  exportAspectRatio,
  exportQuality = 'best',
  exportFraming,
  exportClipVideoToSubtitle = false,
  videoBookends = null,
  audioTrackMeta = null,
  editorAnimationSpeed = null,
}) {
  const clipTexts = (videoTracks || [])
    .flat()
    .map((c) => String(c.narrationText || c.sceneText || '').trim())
    .filter(Boolean);
  const scriptLanguageRaw = resolveVideoProjectScriptLanguage(story, scenes, clipTexts);
  const scriptLanguage = scriptLanguageRaw || null;

  const storyMinimal = story
    ? {
        id: story.id,
        title: story.title,
        tone: story.tone,
        visual_style: story.visual_style,
        story_type: story.story_type,
        ...(scriptLanguage ? { language: scriptLanguage } : {}),
        ...(story.music_url
          ? {
            music_url: persistedMediaUrl(story.music_url) || story.music_url,
          }
          : {}),
        ...(story.music_mood ? { music_mood: story.music_mood } : {}),
        ...(story.music_prompt ? { music_prompt: story.music_prompt } : {}),
        ...(Array.isArray(story.music_history) && story.music_history.length
          ? { music_history: JSON.parse(JSON.stringify(story.music_history)) }
          : {}),
        ...(story.music_target_duration != null ? { music_target_duration: story.music_target_duration } : {}),
        ...(story.total_duration != null ? { total_duration: story.total_duration } : {}),
        ...(story.final_video_url
          ? {
            final_video_url: persistedMediaUrl(story.final_video_url) || story.final_video_url,
          }
          : {}),
        ...(story.news_pack && typeof story.news_pack === 'object'
          ? { news_pack: JSON.parse(JSON.stringify(story.news_pack)) }
          : {}),
      }
    : { id: storyId, title: null };

  const draft = {
    format: VIDEO_PROJECT_FORMAT,
    version: VIDEO_PROJECT_VERSION,
    savedAt: new Date().toISOString(),
    storyId,
    story: storyMinimal,
    scenes: Array.isArray(scenes) ? JSON.parse(JSON.stringify(scenes)) : [],
    editor: {
      videoTracks: JSON.parse(JSON.stringify(videoTracks || [[]])),
      audioTracks: JSON.parse(JSON.stringify(audioTracks || [[]])),
      musicTracks: JSON.parse(JSON.stringify(musicTracks || [[]])),
      subtitleTracks: JSON.parse(JSON.stringify(subtitleTracks || [[]])),
      effectTracks: JSON.parse(JSON.stringify(effectTracks || [[]])),
      overlayTracks: JSON.parse(JSON.stringify(overlayTracks || [[]])),
      subtitleStyle: normalizeVideoSubtitleStyle(subtitleStyle),
      timelineZoom: timelineZoom ?? 1,
      timelineActive: {
        image: true,
        video: true,
        audio: true,
        music: true,
        sfx: true,
        subtitle: true,
        subtitleLook: true,
        subtitleLookMinimized: false,
        effects: true,
        animation: true,
        hostOverlay: true,
        ...(timelineActive || {}),
      },
      volume: volume ?? 0.8,
      masterAudioPlaybackSpeed: masterAudioPlaybackSpeed ?? 1,
      isMuted: Boolean(isMuted),
      videoMasterMuted: Boolean(videoMasterMuted),
      exportAspectRatio: exportAspectRatio || '16:9',
      exportQuality: exportQuality || 'best',
      exportFraming: normalizeVideoExportFraming(exportFraming),
      exportClipVideoToSubtitle: Boolean(exportClipVideoToSubtitle),
      videoBookends: persistVideoBookendsForStorage(videoBookends, {
        defaultTitle: storyMinimal?.title || '',
        aspectRatio: exportAspectRatio || '16:9',
      }),
      ...(Array.isArray(audioTrackMeta) && audioTrackMeta.length
        ? { audioTrackMeta: JSON.parse(JSON.stringify(audioTrackMeta)) }
        : {}),
      editor_animation_speed: editorAnimationSpeed != null
        ? clampEditorAnimationSpeed(editorAnimationSpeed)
        : EDITOR_ANIMATION_SPEED_DEFAULT,
    },
  };
  const enriched = enrichProjectDocWithTimelineAudio(draft);
  return finalizeDocPersistedMedia(enriched);
}

export function parseVideoProjectDocument(text) {
  let obj;
  try {
    obj = JSON.parse(text);
  } catch {
    return { ok: false, error: 'Invalid JSON' };
  }
  if (!obj || obj.format !== VIDEO_PROJECT_FORMAT) {
    return { ok: false, error: 'Not a Twin Tales video project file' };
  }
  if (obj.version !== VIDEO_PROJECT_VERSION) {
    return { ok: false, error: `Unsupported project version: ${obj.version}` };
  }
  if (!obj.storyId || !obj.editor) {
    return { ok: false, error: 'Project file is missing story or editor data' };
  }
  return { ok: true, doc: obj };
}

export function downloadVideoProjectJson(doc, filename) {
  const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename.replace(/[/\\?%*:|"<>]/g, '_');
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
}

export function setPendingVideoProjectNavigation(doc) {
  sessionStorage.setItem(PENDING_PROJECT_KEY, JSON.stringify(doc));
}

export function readAndClearPendingVideoProject(storyId) {
  const raw = sessionStorage.getItem(PENDING_PROJECT_KEY);
  if (!raw) return null;
  try {
    const doc = JSON.parse(raw);
    if (doc?.storyId === storyId && doc?.format === VIDEO_PROJECT_FORMAT) {
      sessionStorage.removeItem(PENDING_PROJECT_KEY);
      return doc;
    }
  } catch {
    sessionStorage.removeItem(PENDING_PROJECT_KEY);
  }
  return null;
}
